import type { ReactNode } from 'react';
import './StatTile.css';

export type StatTileTone = 'neutral' | 'success' | 'warning' | 'danger' | 'accent';

export interface StatTileProps {
  /** The uppercase micro-label. Short — it is set at 11px and does not wrap well. */
  label: ReactNode;
  /**
   * The figure. Pass a `Money` or `Mono` element for currency and identifiers;
   * plain numbers get tabular figures from this component.
   */
  value: ReactNode;
  /** A sentence of context under the figure. */
  note?: ReactNode;
  /** Colours the *figure*, not the tile — the tone is what the number means. */
  tone?: StatTileTone;
  /**
   * Variants, all five of which the prototype uses:
   *
   *   `plain`      bordered white tile (the default)
   *   `tinted`     tone-tinted background, label takes the tone, value stays primary
   *   `alarm`      tone-coloured *border* — for when the figure is itself the problem
   *   `bare`       no container at all, for use inside a parent card's summary grid
   *   `placeholder` dashed, with an em-dash value, for "not measured yet"
   */
  variant?: 'plain' | 'tinted' | 'alarm' | 'bare' | 'placeholder';
  /**
   * Makes the whole tile a button.
   *
   * content.md and 08 §7 both require that a figure leads somewhere — "no
   * dead-end numbers". A tile showing 2,340 unresolved pairs that cannot be
   * clicked forces the reader to go and find the list themselves, which is the
   * behaviour those rules exist to prevent. The prototype does this on the
   * reconciliation sweep, where each KPI selects the matching tab below.
   */
  onSelect?: () => void;
  /**
   * Overrides the button's accessible name. Needed because the visible content is
   * "MISSING IN HUBSPOT / 2,340", which announces as a bare number with no verb —
   * a screen-reader user needs "Show the 2,340 records missing in HubSpot".
   */
  actionLabel?: string;
}

/**
 * A single figure with its label and context (design-system catalogue, Tier 3).
 *
 * **14 of the prototype's 18 page files.** Deliberately not `Card`: measured at a
 * 14px radius and 16px padding against Card's 16px/20px, and its internal rhythm
 * is label → figure → note rather than a title with arbitrary content
 * (prototype/CENSUS.md §2a item 5).
 *
 * The figure is always tabular. A dashboard tile that re-renders with a new value
 * must not change width as it does — proportional digits make a polling number
 * jitter, which reads as instability in the data rather than in the typeface.
 */
export function StatTile({
  label,
  value,
  note,
  tone = 'neutral',
  variant = 'plain',
  onSelect,
  actionLabel,
}: StatTileProps) {
  const className = [
    'dpr-stat',
    `dpr-stat--${variant}`,
    `dpr-stat--tone-${tone}`,
    onSelect ? 'dpr-stat--interactive' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <span className="dpr-stat__label">{label}</span>
      <span className="dpr-stat__value">{variant === 'placeholder' ? '—' : value}</span>
      {note ? <span className="dpr-stat__note">{note}</span> : null}
    </>
  );

  if (onSelect) {
    return (
      <button type="button" className={className} onClick={onSelect} aria-label={actionLabel}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}

export interface StatGridProps {
  children: ReactNode;
  /**
   * The minimum tile width before the grid drops a column. The prototype uses
   * 150–230px depending on how long its figures are; 170px is the median and the
   * right default. This is a `minmax` floor, not a fixed width — the grid is
   * `auto-fit`, so it reflows continuously instead of at breakpoints, which is why
   * no media query appears in this component at all.
   */
  min?: number;
}

/** The `auto-fit` grid these sit in. */
export function StatGrid({ children, min = 170 }: StatGridProps) {
  return (
    <div className="dpr-stat-grid" style={{ '--dpr-stat-min': `${min}px` } as React.CSSProperties}>
      {children}
    </div>
  );
}
